import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  OrgSwitcher as BaseOrgSwitcher,
  type OrgSwitcherOrganization,
} from '@agion/ui'
import { organizationApi } from '../../services/organization-api'

interface OrgSwitcherProps {
  collapsed?: boolean
  currentOrgId?: string
}

export const OrgSwitcher: React.FC<OrgSwitcherProps> = ({ collapsed = false, currentOrgId }) => {
  const { t } = useTranslation('common')
  const [organizations, setOrganizations] = React.useState<OrgSwitcherOrganization[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | undefined>()

  const load = React.useCallback(async () => {
    try {
      setIsLoading(true)
      setError(undefined)
      const orgs = await organizationApi.listUserOrganizations()
      setOrganizations(orgs)
    } catch (err) {
      console.error('Failed to load organizations:', err)
      setError(t('orgSwitcher.loadError', { defaultValue: 'Failed to load organizations' }))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  React.useEffect(() => { void load() }, [load])

  const handleSwitch = async (orgId: string) => {
    await organizationApi.switchOrganization(orgId)
    window.location.reload()
  }

  return (
    <BaseOrgSwitcher
      organizations={organizations}
      currentOrgId={currentOrgId}
      onSwitch={handleSwitch}
      variant="sidebar"
      collapsed={collapsed}
      isLoading={isLoading}
      error={error}
      onRetry={load}
      labels={{
        selectOrg: t('orgSwitcher.selectOrg', { defaultValue: 'Select Organization' }),
        title: t('orgSwitcher.title', { defaultValue: 'Organizations' }),
        description: t('orgSwitcher.description', { defaultValue: 'Switch between your organizations' }),
        tooltip: t('orgSwitcher.tooltip', { defaultValue: 'Switch organization' }),
        retry: t('orgSwitcher.retry', { defaultValue: 'Retry' }),
      }}
    />
  )
}
